import { supabase } from '../lib/supabase';

/**
 * Service for interacting with use cases and AI tools data
 * Handles snake_case (database) to camelCase (React) conversion
 */
export const useCaseService = {
  /**
   * Get all use cases
   * @returns {Promise<Array>} Array of use cases in camelCase
   */
  async getAllUseCases() {
    const { data, error } = await supabase?.from('use_cases')?.select('*')?.order('name', { ascending: true });
    
    if (error) throw error;
    
    // Convert snake_case to camelCase
    return data?.map(row => ({
      id: row?.id,
      name: row?.name,
      description: row?.description,
      icon: row?.icon,
      createdAt: row?.created_at
    })) || [];
  },

  /**
   * Get all AI tools
   * @returns {Promise<Array>} Array of AI tools in camelCase
   */
  async getAllAITools() {
    const { data, error } = await supabase?.from('ai_tools')?.select('*')?.order('name', { ascending: true });
    
    if (error) throw error;
    
    // Convert snake_case to camelCase
    return data?.map(row => ({
      id: row?.id,
      name: row?.name,
      description: row?.description,
      value: row?.value,
      createdAt: row?.created_at
    })) || [];
  },

  /**
   * Get AI tools for a specific use case
   * @param {string} useCaseId - UUID of the use case
   * @returns {Promise<Array>} Array of AI tools for the use case
   */
  async getAIToolsByUseCase(useCaseId) {
    const { data, error } = await supabase
      ?.from('use_case_ai_tools')
      ?.select(`
        ai_tools (
          id,
          name,
          description,
          value,
          created_at
        )
      `)
      ?.eq('use_case_id', useCaseId);
    
    if (error) throw error;
    
    // Extract and convert AI tools
    return data?.map(row => ({
      id: row?.ai_tools?.id,
      name: row?.ai_tools?.name,
      description: row?.ai_tools?.description,
      value: row?.ai_tools?.value,
      createdAt: row?.ai_tools?.created_at
    })) || [];
  },

  /**
   * Get use cases for a specific AI tool
   * @param {string} aiToolValue - Value of the AI tool (e.g., 'chatgpt', 'claude')
   * @returns {Promise<Array>} Array of use cases for the AI tool
   */
  async getUseCasesByAITool(aiToolValue) {
    // First get the AI tool ID
    const { data: toolData, error: toolError } = await supabase?.from('ai_tools')?.select('id')?.eq('value', aiToolValue)?.single();
    
    if (toolError) throw toolError;
    if (!toolData) return [];
    
    // Then get use cases for this tool
    const { data, error } = await supabase?.from('use_case_ai_tools')?.select(`
        use_cases (
          id,
          name,
          description,
          icon,
          created_at
        )
      `)?.eq('ai_tool_id', toolData?.id);
    
    if (error) throw error;
    
    // Extract and convert use cases
    return data?.map(row => ({
      id: row?.use_cases?.id,
      name: row?.use_cases?.name,
      description: row?.use_cases?.description,
      icon: row?.use_cases?.icon,
      createdAt: row?.use_cases?.created_at
    })) || [];
  },

  /**
   * Get complete mapping of all use cases with their AI tools
   * @returns {Promise<Array>} Array of use cases with nested AI tools
   */
  async getUseCasesWithTools() {
    const { data, error } = await supabase?.from('use_cases')?.select(`
        id,
        name,
        description,
        icon,
        created_at,
        use_case_ai_tools (
          ai_tools (
            id,
            name,
            description,
            value,
            created_at
          )
        )
      `)?.order('name', { ascending: true });
    
    if (error) throw error;
    
    // Convert to camelCase with nested tools
    return data?.map(useCase => ({
      id: useCase?.id,
      name: useCase?.name,
      description: useCase?.description,
      icon: useCase?.icon,
      createdAt: useCase?.created_at,
      aiTools: useCase?.use_case_ai_tools?.map(relation => ({
        id: relation?.ai_tools?.id,
        name: relation?.ai_tools?.name,
        description: relation?.ai_tools?.description,
        value: relation?.ai_tools?.value,
        createdAt: relation?.ai_tools?.created_at
      })) || []
    })) || [];
  }
};